<template>
	<v-theme-provider :dark="effectiveAppearance === 'dark'" :light="effectiveAppearance === 'light'">
	<div
		class="agreement-builder"
		:class="{ 'brand-agreement': branding, 'agreement-builder--frame': inFrame, 'agreement-builder--headerless': !showHeader, 'agreement-builder--paged': paged }"
		v-if="agreement"
		:key="agreement.id"
		:style="`--v-zoom: ${zoom / 100}; ${styles}; ${brandingStyle}`"
	>
		<!-- Page by page, the progress bar replaces the header: Finish lives in the bottom bar -->
		<div v-if="paged" class="agreement-progress d-print-none" role="progressbar" aria-valuemin="0" :aria-valuemax="agreement.pages.length" :aria-valuenow="currentPage + 1" :aria-label="pageProgressLabel">
			<span :style="`width: ${progress}%`"></span>
		</div>
		<div v-if="showHeader" class="header-area">
			<div class="header-left" v-if="$vuetify.breakpoint.mdAndUp">
				<span class="agreement-mark"><portal-icon name="handshake" :size="20" /></span>
				<span class="agreement-name" :style="`font-family: '${agreement.styles.h1.fontFamily}'`">{{ agreement.name }}</span>
				<button
					class="agreement-icon-button d-print-none"
					type="button"
					:aria-label="$t('proposals.print-save-pdf')"
					@click="$emit('print')"
				>
					<portal-icon name="file-down" :size="19" />
				</button>
			</div>
			<div class="header-right" v-if="signer">
				<span class="finish-warning" v-if="showFinishWarning && !paged"><portal-icon name="arrow-right" :size="22" /></span>
				<!-- Page-by-page mode finishes from the last page's bar instead -->
				<button v-if="!signer.locked && !paged" class="agreement-finish" type="button" :disabled="packageOffersLoading" @click="finalize">
					<span :style="`font-family: '${agreement.styles.h1.fontFamily}'`">{{
						agreement.settings.finishedButton ? agreement.settings.finishedButton : 'Finish'
					}}</span>
				</button>
				<div v-else-if="signer.locked" class="agreement-complete">
					<portal-icon name="circle-check" :size="18" />
					<span :style="`font-family: '${agreement.styles.h1.fontFamily}'`">{{
						DateTime.fromISO(signer.timestamp).toLocaleString(DateTime.DATETIME_MED)
					}}</span>
				</div>
			</div>
		</div>
		<div class="agreement-content row-format">
			<div
				class="agreement-index column-format"
				v-if="!inFrame && indexedPages.length && $vuetify.breakpoint.mdAndUp"
			>
				<ul class="agreement-index__list">
					<li
						v-for="page in indexedPages"
						:key="page.index"
						class="nav-link"
						:class="{ 'nav-link--current': paged && page.index === currentPage }"
						:style="`font-family: '${agreement.styles.h1.fontFamily}'`"
						@click="scrollToPage(page.index)"
					>
						<span class="agreement-index__number">{{ page.index + 1 }}</span>
						{{ page.title }}
					</li>
				</ul>
			</div>
			<div class="agreement-page-area show-scrollbar" :class="{ 'agreement-page-area--paged': paged }" id="page-area">
				<div class="zoom agreement-pages">
					<div id="print-area">
						<div
							v-for="(page, index) in agreement.pages"
							v-show="!paged || index === currentPage"
							:key="page.id"
							:id="`page-${index}`"
							class="column-format centered"
						>
							<page
								:id="`page-${index}`"
								:ref="`page-${index}`"
								:page-index="index"
								:agreement="agreement"
								:mce-config="{}"
								:client-mode="clientMode"
								:signer="signer"
								:edit-mode="false"
								:template-mode="false"
								:tokens="tokens"
								:page-width="pageWidth"
								:viewer-service="viewerService"
								:in-frame="inFrame"
								@container-item-signed="itemSigned(page, $event)"
								@signed="itemSigned(page, $event)"
								@package-offer-quote-state="setPackageOfferQuoteState"
							></page>
							<div class="page-break"></div>
						</div>
					</div>
					<div class="row-format centered my-4" v-if="signer && !paged">
						<button v-if="!signer.locked" class="agreement-finish agreement-finish--large" type="button" :disabled="packageOffersLoading" @click="finalize">
							<span :style="`font-family: '${agreement.styles.h1.fontFamily}'`">{{
								agreement.settings.finishedButton ? agreement.settings.finishedButton : 'Finish'
							}}</span>
						</button>
					</div>
				</div>
				<!-- Page-by-page navigation: every page stays mounted (signatures, forms and finalize
					span them all); only the current one shows. Printing renders every page. The bar
					sticks to the bottom of the scroll area so the buttons never move between pages. -->
				<div v-if="paged" class="agreement-page-nav-bar d-print-none">
					<div class="agreement-page-nav" :class="inFrame || pageWidth <= 800 ? 'px-3' : 'px-12'" :style="`max-width: ${pageNavWidth}`">
						<button v-if="currentPage > 0" class="agreement-back" type="button" @click="goToPage(currentPage - 1)">
							<portal-icon name="arrow-left" :size="18" />
							<span :style="`font-family: '${agreement.styles.h1.fontFamily}'`">{{ agreement.settings.previousPageButton || $t('proposals.page-back') }}</span>
						</button>
						<span v-else></span>
						<span class="agreement-page-count">{{ pageCountLabel }}</span>
						<button v-if="!lastPage" class="agreement-finish agreement-finish--large agreement-next" type="button" @click="goToPage(currentPage + 1)">
							<span :style="`font-family: '${agreement.styles.h1.fontFamily}'`">{{ agreement.settings.nextPageButton || $t('proposals.page-next') }}</span>
							<portal-icon name="arrow-right" :size="18" />
						</button>
						<button
							v-else-if="signer && !signer.locked"
							class="agreement-finish agreement-finish--large agreement-next"
							type="button"
							:disabled="packageOffersLoading"
							@click="finalize"
						>
							<span :style="`font-family: '${agreement.styles.h1.fontFamily}'`">{{
								agreement.settings.finishedButton ? agreement.settings.finishedButton : 'Finish'
							}}</span>
						</button>
						<div v-else-if="signer && signer.locked" class="agreement-page-nav-done">
							<div class="agreement-complete">
								<portal-icon name="circle-check" :size="18" />
								<span :style="`font-family: '${agreement.styles.h1.fontFamily}'`">{{
									DateTime.fromISO(signer.timestamp).toLocaleString(DateTime.DATETIME_MED)
								}}</span>
							</div>
							<!-- Page by page has no header, so the signed copy downloads from here -->
							<button
								v-if="agreement.fullyExecuted"
								class="agreement-icon-button"
								type="button"
								:aria-label="$t('proposals.print-save-pdf')"
								:title="$t('proposals.print-save-pdf')"
								@click="$emit('print')"
							>
								<portal-icon name="file-down" :size="19" />
							</button>
						</div>
						<span v-else></span>
					</div>
				</div>
			</div>
		</div>
		<div
			class="agreement-zoom d-print-none"
			:class="{ 'agreement-zoom--raised': paged }"
			v-if="agreement.styles.pageSize !== 'RESPONSIVE'"
		>
			<button type="button" @click="zoomOut()"><portal-icon name="zoom-out" :size="18" /></button>
			<span>{{ zoom }}%</span>
			<button type="button" @click="zoomIn()"><portal-icon name="zoom-in" :size="18" /></button>
		</div>
	</div>
	</v-theme-provider>
</template>

<script>
	import { DateTime } from 'luxon';
	import { applyPackageOfferTokens, mergePackageOfferResult } from '@/modules/agreements/packageOfferFinalization';
	import { commercialDecisionWarning } from '@/modules/agreements/commercialDecision';
	import Page from '@/modules/agreements/schema/Page';
	import AgreementMixin from '@/modules/agreements/schema/AgreementMixin';
	import ConfirmModal from '@/components/ConfirmModal';
	import PortalIcon from '@/components/PortalIcon';
	import { agreementBranding, agreementStyles, invoiceBrandStyle } from '@moxie/branding';

	export default {
		name: 'AgreementRenderer',

		// inFrame: embedded on a website (?inFrame=true) — no header or page list, pages edge to edge
		props: ['agreement', 'signer', 'showFinishWarning', 'viewerService', 'appearance', 'inFrame'],

		mixins: [AgreementMixin],

		components: { Page, PortalIcon },

		data: function() {
			return {
				zoom: 100,
				currentPage: 0,
				DateTime: DateTime,
				pageWidth: 0,
				resizeObserver: null,
				packageOfferQuoteStates: {},
			};
		},

		mounted() {
			this.initializeZoom();
			addEventListener('resize', () => this.initializeZoom());

			this.resizeObserver = new ResizeObserver(() => {
				this.setPageWidth();
			});

			this.$nextTick(() => this.listenForPageChanges());
		},

		beforeDestroy() {
			if (this.resizeObserver) this.resizeObserver.disconnect();
		},

		methods: {
			commercialDecisionWarning: function() {
				return commercialDecisionWarning(this.agreement, this.$t.bind(this));
			},
			setPackageOfferQuoteState: function(state) {
				// Keep the last ready quote while re-quoting so dependent blocks (e.g. the Stripe element) stay mounted.
				const previous = this.packageOfferQuoteStates[state.id];
				const keepPrevious = state.loading && previous && previous.ready;
				this.$set(this.packageOfferQuoteStates, state.id, keepPrevious ? { ...previous, loading: true } : state);
			},
			listenForPageChanges: function() {
				this.$nextTick(() => {
					const page = document.getElementById('page-area');
					if (!page) return;
					this.resizeObserver.observe(page);
					this.setPageWidth();
				});
			},

			setPageWidth: function() {
				const page = document.getElementById('page-area');
				if (!page) return;
				this.pageWidth = page.offsetWidth;
			},

			async finalize() {
				try {
					const decisionWarning = this.commercialDecisionWarning();
					if (decisionWarning) {
						this.$store.commit('error', decisionWarning);
						return;
					}
					delete this.agreement.fileUploads;

					// Only enforce package selection when the agreement actually offers packages to select.
					const hasSelectablePackages = this.getServices(this.agreement).some((service) => service.packages && service.packages.length);
					if (!this.packageOfferIds.length && hasSelectablePackages && this.agreement.settings.requireMinimumPackageSelection) {
						const selectedPackages = this.getSelectedPackages(this.agreement);
						if (selectedPackages.length === 0) {
							this.$store.commit('error', this.agreement.settings.noPackageSelectionError);
							return;
						}
					}

					let packageOffer = null;
					for (let i = 0; i < this.agreement.pages.length; i++) {
						let result;
						try {
							result = await this.$refs['page-' + i][0].finalize();
						} catch (err) {
							if (this.paged) this.revealProblem(i);
							throw err;
						}
						packageOffer = mergePackageOfferResult(packageOffer, result);
					}

					this.$emit('finalize', { packageOffer });
				} catch (err) {
					console.log(err);
					console.log('Error finalizing agreement: ' + err);
				}
			},

			async reset() {
				try {
          delete this.agreement.fileUploads;

					for (let i = 0; i < this.agreement.pages.length; i++) {
						await this.$refs['page-' + i][0].reset();
					}
				} catch (err) {
					console.log(err);
					console.log('Error resetting agreement: ' + err);
				}
			},

			// afterSigned: where to go when there's no redirect (a public link opens the signed copy);
			// otherwise the page reloads to show it.
			handlePostSignature: function(afterSigned = null) {
				if (this.agreement.settings.signedConfirmationMessage) {
					let binding = {
						bodyText: this.agreement.settings.signedConfirmationMessage,
						hideNo: true,
						yesText: 'OK',
					};
					this.$store.state.globalModalController.openModal(ConfirmModal, binding).then(() => {
						this.handleOptionalRedirect(afterSigned);
					});
				} else {
					this.handleOptionalRedirect(afterSigned);
				}
			},

			handleOptionalRedirect: function(afterSigned = null) {
				const redirectUrl = this.agreement.settings.redirectUrl;
				if (redirectUrl && window.self !== window.top) {
					// Embedded on a website: the host page's embed snippet follows the redirect
					window.parent.postMessage('[Redirect]' + redirectUrl, '*');
				} else if (redirectUrl) {
					window.location.href = redirectUrl;
				} else if (afterSigned) {
					afterSigned();
				} else {
					setTimeout(() => window.location.reload(), 5000);
				}
			},

			itemSigned: function(page, item) {
				let signer = item.signer;
				let signature = item.signature;
				//signature.containerTarget = item.containerTarget;

				let signerIx = this.agreement.signers.findIndex((s) => s.id === signer.id);
				let signerRecord = this.agreement.signers[signerIx];
				signerRecord.signature = signer.signature;
				signerRecord.signatureType = signer.signatureType;
				signerRecord.initials = signer.initials;
				signerRecord.title = signer.title;
				signerRecord.timestamp = DateTime.now().toISO();
				this.agreement.signers.splice(signerIx, 1, signerRecord);

				if (signature.signedBy.indexOf(signer.id) === -1) {
					signature.signedBy.push(signer.id);
				}
			},

			scrollToPage: function(index) {
				if (this.paged) {
					this.goToPage(index);
					return;
				}
				document.getElementById(`page-${index}`).scrollIntoView({ block: 'start', behavior: 'smooth' });
			},

			goToPage: function(index) {
				this.currentPage = Math.max(0, Math.min(index, this.agreement.pages.length - 1));
				this.$nextTick(() => {
					const pageArea = document.getElementById('page-area');
					if (pageArea) pageArea.scrollTo({ top: 0 });
				});
			},

			// A block that fails validation marks itself (red-border) and scrolls to itself, but
			// can't while its page is hidden: show that page, then bring the block into view.
			revealProblem: function(index) {
				this.goToPage(index);
				this.$nextTick(() => {
					const problem = document.querySelector(`#page-${index} .red-border`);
					if (problem) problem.scrollIntoView({ block: 'start', behavior: 'smooth' });
				});
			},

			zoomIn: function() {
				this.zoom = this.zoom + 10;
			},

			zoomOut: function() {
				if (this.zoom > 10) {
					this.zoom = this.zoom - 10;
				}
			},

			handleGestureEvent: function(e) {
				console.log(e);
				console.log(e.scale);
				// node.addEventListener('gestureend', function(e) {
				//   if (e.scale < 1.0) {
				//     // User moved fingers closer together
				//   } else if (e.scale > 1.0) {
				//     // User moved fingers further apart
				//   }
				// }, false);
			},

			initializeZoom: function() {
				if (this.agreement.styles.pageSize === 'RESPONSIVE') {
					return;
				}
				let browserWidth = document.getElementById('page-area').offsetWidth;

				let targetZoom = parseInt((browserWidth / this.widthInPixels) * 100, 10) - 5;
				if (targetZoom <= 100) {
					this.zoom = targetZoom;
					setTimeout(() => {
						let pageArea = document.getElementById('page-area');
						pageArea.scrollLeft = (pageArea.scrollWidth - pageArea.clientWidth) / 2;
					}, 500);
				}
			},
		},

		watch: {
			'$vuetify.breakpoint.mdAndUp': function() {
				this.$nextTick(() => this.initializeZoom());
			},
		},

		computed: {
			effectiveAppearance() {
				return this.appearance || (this.$vuetify.theme.dark ? 'dark' : 'light');
			},
			branding() { return agreementBranding(this.agreement); },
			brandingStyle() { return Object.entries(invoiceBrandStyle(this.branding)).map(([key, value]) => `${key}:${value}`).join(';'); },
			packageOfferIds: function() {
				return this.agreement.pages.reduce((ids, page) => ids.concat(page.schema.filter((item) => item.type === 'PackageOffer').map((item) => item.id)), []);
			},
			packageOffersLoading: function() {
				return this.packageOfferIds.some((id) => !this.packageOfferQuoteStates[id] || this.packageOfferQuoteStates[id].loading === true);
			},
			tokens: function() {
				const tokens = this.getTokensFromAgreement(this.agreement);
				const quoteState = Object.values(this.packageOfferQuoteStates).find((state) => state.ready && state.quote);
				if (!quoteState) return tokens;
				return applyPackageOfferTokens(
					tokens,
					quoteState.quote,
					(amount, currency) => this.$formatters.dollars(amount, true, true, currency),
					quoteState.paymentChoiceLabel
				);
			},

			widthInPixels: function() {
				if (this.agreement.styles.pageSize === 'US_LETTER') {
					return this.agreement.styles.landscape ? 1054 : 816;
				} else {
					return this.agreement.styles.landscape ? 1123 : 794;
				}
			},

			indexedPages: function() {
				let result = [];
				this.agreement.pages.forEach((p, index) => {
					result.push({ title: p.title, index: index });
				});
				return result.filter((r) => r.title);
			},

			styles: function() {
				const resolved = agreementStyles(this.agreement);
				let { body, h1, h2, h3 } = resolved;
				const h4 = { fontFamily: 'Inter', fontSize: 20, fontWeight: 600, color: '#000000', lineHeight: 1.5, ...resolved.h4 };
				let baseCSS = `--primary-brand-color: ${this.branding ? this.branding.colors.primary : this.$store.getters.getCustomBranding.primaryColor}; --canvas-color: ${resolved.canvasColor}; --page-color: ${resolved.pageColor}; `;
				let bodyCSS = `--body-size: ${body.fontSize}px; --body-weight: ${body.fontWeight}; --body-font: "${body.fontFamily}"; --body-color: ${body.color}; --body-height: ${body.lineHeight}; `;
				let h1CSS = `--h1-size: ${h1.fontSize}px; --h1-weight: ${h1.fontWeight}; --h1-font: "${h1.fontFamily}"; --h1-color: ${h1.color}; --h1-height: ${h1.lineHeight};  `;
				let h2CSS = `--h2-size: ${h2.fontSize}px; --h2-weight: ${h2.fontWeight}; --h2-font: "${h2.fontFamily}"; --h2-color: ${h2.color}; --h2-height: ${h2.lineHeight};  `;
				let h3CSS = `--h3-size: ${h3.fontSize}px; --h3-weight: ${h3.fontWeight}; --h3-font: "${h3.fontFamily}"; --h3-color: ${h3.color}; --h3-height: ${h3.lineHeight};  `;

				let h4CSS = `--h4-size: ${h4.fontSize}px; --h4-weight: ${h4.fontWeight}; --h4-font: "${h4.fontFamily}"; --h4-color: ${h4.color}; --h4-height: ${h4.lineHeight}; `;

				return baseCSS + bodyCSS + h1CSS + h2CSS + h3CSS + h4CSS;
			},

			// settings.pageNavigation PAGED: one page at a time. Printing (PDF render or the
			// browser print dialog) always lays out every page.
			paged: function() {
				return (
					!!this.agreement.settings &&
					this.agreement.settings.pageNavigation === 'PAGED' &&
					this.agreement.pages.length > 1 &&
					!this.$store.state.documentPrinting
				);
			},

			// Scrolling agreements keep the header (name, PDF, Finish); page by page and embedded
			// ones don't need it
			showHeader: function() {
				return !this.inFrame && !this.paged;
			},

			lastPage: function() {
				return this.currentPage >= this.agreement.pages.length - 1;
			},

			progress: function() {
				return ((this.currentPage + 1) / this.agreement.pages.length) * 100;
			},

			// "Pricing (2 / 5)", or just "2 / 5" for an untitled page
			pageCountLabel: function() {
				const count = `${this.currentPage + 1} / ${this.agreement.pages.length}`;
				const title = (this.agreement.pages[this.currentPage].title || '').trim();
				return title ? `${title} (${count})` : count;
			},

			pageProgressLabel: function() {
				return this.$t('proposals.page-progress', { current: this.currentPage + 1, total: this.agreement.pages.length });
			},

			// The nav row lines up with the page edges (page width plus the wrapper's side padding)
			pageNavWidth: function() {
				const gutter = this.inFrame ? 0 : 96;
				return this.agreement.styles.pageSize === 'RESPONSIVE'
					? `${this.agreement.styles.maxWidth + gutter}px`
					: `${Math.round(((this.widthInPixels + gutter) * this.zoom) / 100)}px`;
			},

			clientMode: function() {
				return this.signer && this.signer.signerType === 'CLIENT';
			},
		},
	};
</script>

<style lang="scss">
	@import '~@moxie/branding/src/agreement.scss';
	@import './agreement-brand.scss';

	// Primary agreement action (Finish, payment verification). Global so agreement blocks can share it;
	// branded agreements override it in agreement-brand.scss.
	.agreement-builder {
		.agreement-finish {
			min-height: 44px;
			padding: 0 22px;
			border: 0;
			border-radius: 14px;
			background: var(--v-primary-base);
			color: var(--v-white-base);
			font-size: 15px;
			font-weight: 650;
			box-shadow: 0 8px 20px rgba(85, 28, 255, 0.2);

			&:hover {
				filter: brightness(0.96);
			}

			&:disabled {
				cursor: not-allowed;
				opacity: 0.55;
				box-shadow: none;
			}

			&--large {
				min-width: 180px;
				min-height: 50px;
			}
		}
	}

	// Embedded: pages run to the frame's edges, up to the agreement's max width
	.agreement-builder--frame {
		.page {
			margin-top: 0 !important;
			border-radius: 0 !important;
		}
	}

	// Page-by-page mode shows the position in its bottom bar, not under each page
	.agreement-builder .agreement-page-area--paged .page-number {
		display: none;
	}

	@media print {
		// Full bleed: the sheet takes the agreement's page color edge to edge (set as
		// --print-page-color by the viewer). Margins give continuation sheets breathing room;
		// the first sheet starts flush so cover images sit at the top edge.
		@page {
			margin: 0.5in 0;
			background: var(--print-page-color, #fff);
		}

		@page :first {
			margin-top: 0;
		}

		body {
			visibility: hidden;
		}
		#print-area {
			visibility: visible;
			position: absolute;
			left: 0;
			top: 0;

			.page {
				margin: 0 !important;
				width: 100% !important;
				height: 100% !important;
			}

			.page-wrapper {
				padding-top: 12px !important;
			}

			.agreement-page {
				padding: 0 !important;
			}

			.page {
				border-radius: 0 !important;
			}

			.page-number {
				display: none;
			}

			.shadow {
				filter: none !important;
			}

			// Keep only short, self-contained blocks whole. Tall blocks (forms, package offers,
			// schedulers, text, terms, containers, embedded PDFs) must flow: forcing them whole
			// makes Chrome overflow them under the next block. Their rows stay whole instead.
			.item-type-Signature,
			.item-type-AcceptButton,
			.item-type-ImageBlock,
			.item-type-Logo,
			.item-type-Divider,
			.item-type-Spacer,
			.signer-block,
			.agreement-form-embed .mx-brand-form > div[id],
			.package-offer__option,
			.package-offer__add-on,
			img {
				break-inside: avoid;
				page-break-inside: avoid;
			}

			h1,
			h2,
			h3 {
				break-after: avoid;
				page-break-after: avoid;
			}
		}

		.page-break {
			page-break-after: always;
			page-break-inside: avoid;
		}
	}
</style>

<style scoped lang="scss">
	.nav-link {
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 10px;
		border-radius: 12px;
		font-size: 14px;
		font-weight: 550;
		color: var(--v-gray_70-base);
		cursor: pointer;
		transition: 150ms ease;

		&:hover {
			color: var(--v-primary-base);
			background: var(--v-primary_10-base);
		}
	}

	.nav-link--current {
		color: var(--v-primary-base);
		background: var(--v-primary_10-base);
	}

	// The unfilled remainder shows as a track, so how far along the visitor is reads at a glance
	.agreement-progress {
		height: 8px;
		background: var(--v-gray_30-base);

		span {
			display: block;
			height: 100%;
			border-radius: 0 99px 99px 0;
			background: var(--primary-brand-color);
			transition: width 250ms ease;
		}
	}

	// A column whose pages fill the leftover height, so the nav bar rests on the bottom
	// of the window even when the current page is shorter than it
	.agreement-page-area--paged {
		display: flex;
		flex-direction: column;
		height: 100%;

		.agreement-pages {
			flex: 1 0 auto;
		}
	}

	.agreement-page-nav-bar {
		position: sticky;
		left: 0;
		bottom: 0;
		z-index: 202;
		padding: 12px 0;
		border-top: 1px solid var(--v-gray_30-base);
		background: rgba(255, 255, 255, 0.96);
		backdrop-filter: blur(14px);
	}

	.agreement-page-nav {
		width: 100%;
		margin: 0 auto;
		display: grid;
		grid-template-columns: 1fr minmax(0, auto) 1fr;
		align-items: center;
		gap: 12px;

		> :first-child {
			justify-self: start;
		}

		> :last-child {
			justify-self: end;
		}
	}

	.agreement-next {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: 8px;
	}

	.agreement-back {
		min-height: 50px;
		padding: 0 20px;
		display: inline-flex;
		align-items: center;
		gap: 8px;
		border: 1px solid var(--v-gray_30-base);
		border-radius: 14px;
		background: var(--v-white-base);
		color: var(--v-gray_70-base);
		font-size: 15px;
		font-weight: 650;

		&:hover {
			background: var(--v-gray_10-base);
			color: var(--v-gray_90-base);
		}
	}

	.agreement-page-count {
		color: var(--v-gray_70-base);
		font-size: 13px;
		font-weight: 600;
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
	}

	@media screen {
		.zoom {
			transform: scale(var(--v-zoom));
			transform-origin: top;
		}
	}

	.agreement-builder {
		background-color: var(--canvas-color);
		height: var(--vh);
		position: relative;

		.header-area {
			min-height: 72px;
			width: 100%;
			padding: 12px 18px;
			background: rgba(255, 255, 255, 0.96);
			border-bottom: 1px solid var(--v-gray_30-base);
			position: sticky;
			top: 0;
			z-index: 203;
			display: flex;
			justify-content: space-between;
			align-items: center;
			backdrop-filter: blur(14px);

			.header-left {
				text-align: left;
				display: flex;
				align-items: center;
				gap: 12px;
				min-width: 0;
			}

			.header-right {
				display: flex;
				justify-content: flex-end;
				align-items: center;
				gap: 10px;
			}
		}
	}

	.agreement-mark {
		width: 40px;
		height: 40px;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		border-radius: 13px;
		color: var(--v-primary-base);
		background: var(--v-primary_10-base);
	}

	.agreement-name {
		max-width: min(480px, 46vw);
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
		font-size: 18px;
		font-weight: 650;
		color: var(--v-gray_90-base);
	}

	.agreement-icon-button {
		width: 40px;
		height: 40px;
		border: 1px solid var(--v-gray_30-base);
		border-radius: 50%;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		background: var(--v-white-base);
		color: var(--v-gray_70-base);

		&:hover {
			background: var(--v-gray_10-base);
			color: var(--v-gray_90-base);
		}
	}

	.finish-warning {
		color: var(--v-error-base);
		animation: agreement-pulse 1.4s ease-in-out infinite;
	}

	.agreement-page-nav-done {
		display: flex;
		align-items: center;
		gap: 8px;
	}

	.agreement-complete {
		min-height: 42px;
		padding: 0 14px;
		display: flex;
		align-items: center;
		gap: 8px;
		border-radius: 13px;
		background: var(--v-success_10-base);
		color: var(--v-success-base);
		font-size: 13px;
		font-weight: 600;
	}

	.agreement-content {
		height: calc(100% - 72px);
	}

	.agreement-index {
		height: 100%;
		min-width: 256px;
		max-width: 256px;
		padding: 14px 12px;
		border-right: 1px solid var(--v-gray_30-base);
		background: var(--v-white-base);

		&__list {
			padding: 0;
			margin: 0;
			list-style: none;
		}

		&__number {
			width: 26px;
			height: 26px;
			display: inline-flex;
			align-items: center;
			justify-content: center;
			flex: 0 0 auto;
			border-radius: 8px;
			background: var(--v-gray_20-base);
			color: var(--v-gray_60-base);
			font-size: 11px;
			font-weight: 700;
		}
	}

	.agreement-page-area {
		max-height: calc(100vh - 72px);
		width: 100%;
		overflow: auto;
	}

	.agreement-pages {
		min-width: fit-content;
		width: 100%;
		overflow-x: auto;
		padding-top: 20px;
	}

	.agreement-zoom {
		position: absolute;
		right: 20px;
		bottom: 20px;
		z-index: 204;
		display: flex;
		align-items: center;
		gap: 2px;
		padding: 5px;
		border: 1px solid var(--v-gray_30-base);
		border-radius: 16px;
		background: rgba(255, 255, 255, 0.94);
		color: var(--v-gray_70-base);
		box-shadow: 0 8px 24px rgba(22, 24, 35, 0.1);
		backdrop-filter: blur(12px);

		button {
			width: 34px;
			height: 34px;
			display: inline-flex;
			align-items: center;
			justify-content: center;
			border: 0;
			border-radius: 11px;
			background: transparent;
			color: inherit;

			&:hover {
				background: var(--v-gray_20-base);
				color: var(--v-gray_90-base);
			}
		}

		span {
			min-width: 48px;
			text-align: center;
			font-size: 12px;
			font-weight: 650;
		}
	}

	@keyframes agreement-pulse {
		50% {
			transform: translateX(4px);
		}
	}

	@media (max-width: 959px) {
		.agreement-builder .header-area {
			justify-content: flex-end;
			min-height: 64px;
			padding: 10px 12px;
		}

		.agreement-content {
			height: calc(100% - 64px);
		}

		.agreement-page-area {
			max-height: calc(100vh - 64px);
		}

		.agreement-pages {
			padding-top: 12px;
		}

		.agreement-zoom {
			right: 12px;
			bottom: 12px;
		}
	}

	.agreement-zoom.agreement-zoom--raised {
		bottom: 96px;
	}

	.agreement-builder--headerless {
		.agreement-content {
			height: 100%;
		}

		.agreement-page-area {
			max-height: var(--vh, 100vh);
		}

		&.agreement-builder--paged {
			.agreement-content {
				height: calc(100% - 8px);
			}

			.agreement-page-area {
				max-height: calc(var(--vh, 100vh) - 8px);
			}
		}
	}

	.agreement-builder--frame .agreement-pages {
		padding-top: 0;
	}

	@media (max-width: 599px) {
		.agreement-zoom.agreement-zoom--raised {
			bottom: 132px;
		}

		.agreement-page-nav {
			grid-template-columns: 1fr 1fr;

			.agreement-page-count {
				grid-column: 1 / -1;
				grid-row: 1;
				justify-self: center;
			}
		}
	}
</style>
